/**
 * Capacity engine — pure, deterministic sprint-capacity math.
 *
 * Powers the "Plan Sprint" step: how many ideal engineering days a team has in
 * a sprint, how much has been committed, what remains, and — per engineer —
 * how their assigned load compares to their individual capacity (flagging
 * anyone over-committed).
 *
 * All effort/capacity is in ideal engineering days.
 *
 * Capacity model — "bandwidth". A member's sprint capacity is NOT a flat
 * one-ideal-day-per-working-day figure. It is their available working days
 * (sprint working days minus any days they are on call) scaled by a net
 * {@link BANDWIDTH_MULTIPLIER} of ×1.08 ({@link STRETCH_FACTOR} 1.2 for stretch
 * × {@link RANDOMISATION_FACTOR} 0.9 for randomisation), then reduced by the
 * per-member cost of the sprint's COMMON / all-hands tasks (ceremonies, all
 * hands, …). See {@link memberBandwidthDays}. Team capacity is the SUM of every
 * member's bandwidth — not member count × working days.
 *
 * Pure TS — no React, no I/O. Task inputs are a minimal structural subset of
 * the extended {@link Task} so the engine stays decoupled yet type-linked.
 */

import type { Task } from '../../tasks/types';

/**
 * Stretch factor: members are planned to slightly over-commit their raw
 * available days (a sprint-planning convention). Applied to available days.
 */
export const STRETCH_FACTOR = 1.2;

/**
 * Randomisation factor: discounts the stretched figure to absorb the day-to-day
 * variance (context switches, interrupts) that erodes ideal capacity.
 */
export const RANDOMISATION_FACTOR = 0.9;

/**
 * Net bandwidth multiplier applied to a member's available working days:
 * {@link STRETCH_FACTOR} × {@link RANDOMISATION_FACTOR} = 1.08.
 */
export const BANDWIDTH_MULTIPLIER = STRETCH_FACTOR * RANDOMISATION_FACTOR;

/** A minimal member shape for capacity math. */
export interface CapacityMember {
  /** Engineer alias. */
  alias: string;
  /**
   * Days this member is on call during the sprint (entered manually per member
   * per sprint). Subtracted from working days BEFORE the bandwidth multiplier.
   * Missing/invalid → 0.
   */
  oncallDays?: number;
}

/** The task fields capacity math needs (structurally a subset of Task). */
export type CapacityTask = Pick<Task, 'owner' | 'effortDays'>;

/** Per-engineer load vs their individual capacity. */
export interface EngineerLoad {
  /** Engineer alias. */
  alias: string;
  /** Days assigned to this engineer (sum of owned tasks' effort). */
  assignedDays: number;
  /** This engineer's bandwidth for the sprint (ideal days). */
  capacityDays: number;
  /** Remaining capacity (capacity − assigned); may be negative. */
  remainingDays: number;
  /** True when the engineer is assigned more than their capacity. */
  overCapacity: boolean;
}

/** A full capacity summary for a sprint. */
export interface CapacitySummary {
  /** Total team capacity in ideal days (sum of every member's bandwidth). */
  teamCapacityDays: number;
  /** Total committed effort in ideal days. */
  committedDays: number;
  /** Remaining team capacity (may be negative when over-committed). */
  remainingDays: number;
  /** True when committed exceeds team capacity. */
  overCommitted: boolean;
  /** Per-engineer breakdown. */
  perEngineer: EngineerLoad[];
  /** Effort owned by nobody / by non-members (not counted per-engineer). */
  unassignedDays: number;
}

/** Rounds to 2 decimals to keep half-day arithmetic clean. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Coerces a value to a non-negative finite number (missing/invalid → 0). */
function nonNegative(n: number | undefined): number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
}

/** Effort of a single task in ideal days (missing/invalid → 0). */
function taskDays(task: CapacityTask): number {
  return nonNegative(task.effortDays);
}

/**
 * A single member's sprint capacity ("bandwidth") in ideal days:
 *
 *   round2( max(0, (workingDays − oncallDays) × BANDWIDTH_MULTIPLIER
 *                    − commonReductionDays) )
 *
 * i.e. on-call days are removed first, the remaining available days are scaled
 * by the ×1.08 net multiplier, and the per-member cost of the sprint's common /
 * all-hands tasks is subtracted last. The result is floored at 0 (a member can
 * never have negative bandwidth) and rounded to 2 decimals. All negative/invalid
 * inputs are treated as 0.
 */
export function memberBandwidthDays(
  workingDays: number,
  oncallDays = 0,
  commonReductionDays = 0,
): number {
  const available = Math.max(0, nonNegative(workingDays) - nonNegative(oncallDays));
  const bandwidth =
    available * BANDWIDTH_MULTIPLIER - nonNegative(commonReductionDays);
  return round2(Math.max(0, bandwidth));
}

/**
 * Total team capacity in ideal days = SUM of every member's bandwidth (see
 * {@link memberBandwidthDays}). `commonReductionDays` (the summed per-member
 * cost of the sprint's common tasks) reduces every member's bandwidth.
 */
export function teamCapacityDays(
  members: CapacityMember[],
  sprintWorkingDays: number,
  commonReductionDays = 0,
): number {
  return round2(
    (members ?? []).reduce(
      (sum, m) =>
        sum +
        memberBandwidthDays(sprintWorkingDays, m.oncallDays, commonReductionDays),
      0,
    ),
  );
}

/** Total committed effort in ideal days (sum of tasks' effort). */
export function committedDays(tasks: CapacityTask[]): number {
  return round2((tasks ?? []).reduce((sum, t) => sum + taskDays(t), 0));
}

/**
 * Remaining team capacity in ideal days (team capacity − committed). May be
 * negative when the team is over-committed.
 */
export function remainingCapacityDays(
  members: CapacityMember[],
  sprintWorkingDays: number,
  tasks: CapacityTask[],
  commonReductionDays = 0,
): number {
  return round2(
    teamCapacityDays(members, sprintWorkingDays, commonReductionDays) -
      committedDays(tasks),
  );
}

/**
 * Per-engineer load: for each member, sums the effort of the tasks they own,
 * compares it to their individual bandwidth (see {@link memberBandwidthDays}),
 * and flags over-capacity. Tasks owned by a non-member or with no owner are
 * summed into the summary's `unassignedDays` (never silently attributed to a
 * member). `commonReductionDays` reduces every member's bandwidth.
 */
export function engineerLoads(
  members: CapacityMember[],
  sprintWorkingDays: number,
  tasks: CapacityTask[],
  commonReductionDays = 0,
): EngineerLoad[] {
  const byAlias = new Map<string, number>();
  for (const member of members ?? []) {
    byAlias.set(member.alias, 0);
  }
  for (const task of tasks ?? []) {
    const owner = task.owner;
    if (owner && byAlias.has(owner)) {
      byAlias.set(owner, (byAlias.get(owner) ?? 0) + taskDays(task));
    }
  }
  return (members ?? []).map((member) => {
    const assignedDays = round2(byAlias.get(member.alias) ?? 0);
    const capacityDays = memberBandwidthDays(
      sprintWorkingDays,
      member.oncallDays,
      commonReductionDays,
    );
    return {
      alias: member.alias,
      assignedDays,
      capacityDays,
      remainingDays: round2(capacityDays - assignedDays),
      overCapacity: assignedDays > capacityDays,
    };
  });
}

/** Effort owned by nobody or by a non-member, in ideal days. */
export function unassignedDays(
  members: CapacityMember[],
  tasks: CapacityTask[],
): number {
  const memberAliases = new Set((members ?? []).map((m) => m.alias));
  return round2(
    (tasks ?? [])
      .filter((t) => !t.owner || !memberAliases.has(t.owner))
      .reduce((sum, t) => sum + taskDays(t), 0),
  );
}

/**
 * Builds the full capacity summary for a sprint in one call — team capacity
 * (sum of bandwidth), committed, remaining, over-commitment flag, per-engineer
 * loads, and the unassigned pool. `commonReductionDays` is the summed per-member
 * cost of the sprint's common / all-hands tasks; it reduces every member's
 * bandwidth.
 */
export function capacitySummary(
  members: CapacityMember[],
  sprintWorkingDays: number,
  tasks: CapacityTask[],
  commonReductionDays = 0,
): CapacitySummary {
  const capacity = teamCapacityDays(members, sprintWorkingDays, commonReductionDays);
  const committed = committedDays(tasks);
  return {
    teamCapacityDays: capacity,
    committedDays: committed,
    remainingDays: round2(capacity - committed),
    overCommitted: committed > capacity,
    perEngineer: engineerLoads(members, sprintWorkingDays, tasks, commonReductionDays),
    unassignedDays: unassignedDays(members, tasks),
  };
}
